// src/pages/RestaurantInfoPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_URL from "../../../config/apiConfig";
import { FaMapMarkerAlt, FaPhone, FaArrowLeft } from "react-icons/fa";

export default function RestaurantInfoPage() {
  const { menuLink } = useParams();
  const navigate = useNavigate();
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        const res = await fetch(`${API_URL}/api/menus/public/${menuLink}`);
        if (!res.ok) throw new Error("مشکل در دریافت اطلاعات رستوران");
        const data = await res.json();
        setRestaurant(data.restaurant);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (menuLink) fetchRestaurant();
    else {
      setError("لینک منو موجود نیست");
      setLoading(false);
    }
  }, [menuLink]);

  if (loading) return <div className="text-center mt-20 text-gray-500">در حال بارگذاری...</div>;
  if (error) return <div className="text-red-500 mt-20 text-center">{error}</div>;
  if (!restaurant) return null;

  const profileImage = restaurant.restaurantSettings?.profileImage
    ? `${API_URL}/uploads/restaurants/${restaurant._id}/${restaurant.restaurantSettings.profileImage}`
    : "/images/default-profile.png";

  const coverImage = restaurant.restaurantSettings?.coverImage
    ? `${API_URL}/uploads/restaurants/${restaurant._id}/${restaurant.restaurantSettings.coverImage}`
    : "/images/default-cover.jpg";

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm p-4 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-700 hover:text-green-600 font-medium"
        >
          <FaArrowLeft /> بازگشت
        </button>
        <div className="flex items-center gap-2">
          <img src={profileImage} alt="logo" className="w-10 h-10 rounded-full object-cover shadow-sm" />
          <span className="font-semibold text-gray-800 text-lg">{restaurant.name}</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto p-4 space-y-6">
        {/* Cover */}
        <div className="w-full h-52 rounded-xl overflow-hidden shadow-sm">
          <img
            src={coverImage}
            alt="cover"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Address */}
        {restaurant.restaurantSettings?.address && (
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <FaMapMarkerAlt /> {restaurant.restaurantSettings.address}
          </div>
        )}

        {/* Description */}
        {restaurant.restaurantSettings?.description && (
          <div className="p-4 bg-white rounded-lg shadow-sm text-gray-700">
            {restaurant.restaurantSettings.description}
          </div>
        )}

        {/* Working Hours & Phone */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {restaurant.restaurantSettings?.workingHours && (
            <div className="p-4 bg-white rounded-lg shadow-sm flex items-center gap-2 text-gray-700">
              🕒 {restaurant.restaurantSettings.workingHours}
            </div>
          )}
          {restaurant.restaurantSettings?.phone && (
            <div className="p-4 bg-white rounded-lg shadow-sm flex items-center gap-2 text-gray-700">
              <FaPhone /> {restaurant.restaurantSettings.phone}
            </div>
          )}
        </div>

        {/* Gallery */}
        {restaurant.restaurantSettings?.gallery?.length > 0 && (
          <div>
            <h2 className="font-semibold text-gray-800 mb-2">گالری تصاویر</h2>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {restaurant.restaurantSettings.gallery.map((img, idx) => (
                <img
                  key={idx}
                  src={`${API_URL}/uploads/restaurants/${restaurant._id}/${img}`}
                  alt={`gallery-${idx}`}
                  className="w-40 h-28 object-cover rounded-lg shadow-sm"
                />
              ))}
            </div>
          </div>
        )}

        {/* Map */}
        {restaurant.restaurantSettings?.location && (
          <div className="mt-4 rounded-lg overflow-hidden shadow-sm">
            <iframe
              title="restaurant-map"
              width="100%"
              height="220"
              className="rounded-lg"
              src={`https://maps.google.com/maps?q=${restaurant.restaurantSettings.location.lat},${restaurant.restaurantSettings.location.lng}&z=15&output=embed`}
              allowFullScreen
            ></iframe>
          </div>
        )}

        {/* Feedback Box */}
        <div className="p-4 bg-white rounded-lg shadow-sm">
          <h2 className="font-semibold mb-3 text-gray-800">نظرات شما</h2>
          <form className="space-y-3">
            <input
              type="text"
              placeholder="نام شما"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-300"
            />
            <input
              type="text"
              placeholder="شماره تماس"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-300"
            />
            <textarea
              placeholder="نظر خود را بنویسید..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-green-300"
            />
            <button
              type="submit"
              className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
            >
              ارسال نظر
            </button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white shadow-inner p-4 text-center text-gray-500 text-sm">
        © 2026  با میزبان • همه حقوق محفوظ است
      </footer>
    </div>
  );
}
